// Describes, for the user, what the chat is about to tell the agent about their
// screen. The agent gets the date range, the applied filters (including their
// values) and the figures on screen — so the person whose data that is should be
// able to see it, not just infer it.
//
// Pure so the wording is testable; rendered by InsightsChatConversation.

import { InsightsView } from 'contextProviders/InsightsContentProvider/viewTypes';

const pluralise = ({ count, noun }: { count: number; noun: string }): string =>
  `${count} ${noun}${count === 1 ? '' : 's'}`;

const describeAttachedView = (
  view: InsightsView,
): { summary: string; detail: string[] } => {
  const dateRange =
    view.startDate && view.endDate
      ? `${view.startDate} to ${view.endDate}`
      : undefined;

  const filterCount = view.filtersTruncatedFrom ?? view.filters.length;
  const gadgetCount = view.gadgetsTruncatedFrom ?? view.gadgets.length;

  const summary = [
    dateRange,
    filterCount > 0
      ? pluralise({ count: filterCount, noun: 'filter' })
      : 'no filters',
    pluralise({ count: gadgetCount, noun: 'gadget' }),
  ]
    .filter(Boolean)
    .join(' · ');

  const detail: string[] = ['The assistant can see:'];
  if (dateRange) {
    detail.push(
      `Dates: ${dateRange}${view.dateField ? ` (on ${view.dateField})` : ''}`,
    );
  }
  if (view.comparison) {
    detail.push(`Comparison: ${view.comparison}`);
  }
  if (view.filters.length) {
    // Spell the filters out rather than counting them: their values are the part
    // being shared, so a count alone would hide what matters.
    view.filters.forEach((filter) =>
      detail.push(`Filter: ${filter.description}`),
    );
    const hidden = filterCount - view.filters.length;
    if (hidden > 0) {
      detail.push(`…and ${pluralise({ count: hidden, noun: 'more filter' })}`);
    }
  }
  if (gadgetCount > 0) {
    detail.push(
      `${pluralise({ count: gadgetCount, noun: 'gadget' })}, with the figures currently shown`,
    );
  }

  return { summary, detail };
};

export default describeAttachedView;
